import React, { useEffect } from "react";
import { Controller, useFieldArray, useFormContext } from "react-hook-form";
import { useStoreState } from "ezStore";
import { useQuery } from "@apollo/client";

import { FormGroup, Input } from "@cofano/cck";
import { Typography } from "@mui/material";

import { FormErrorMessage } from "components/form/FormErrorMessage";
import CountrySelect from "components/selects/CountrySelect";
import RelationSelect from "../selects/RelationSelect";

import { Language, hasPluginPermission, hasOptionalFieldPermission, Fields } from "imports/Utils";

import { RelationSchemaInput, RelationSchemaOutput } from "view/relations/utils/relation.schema";
import { GetInvoiceGroupTypesDocument } from "__generated__/view/invoicegrouptypes/graphql/invoiceGroupType.queries.generated";

const RelationGeneralForm = () => {

	const { control, watch } = useFormContext<RelationSchemaInput, unknown, RelationSchemaOutput>();
	const name = watch("name");
	const { fields, append } = useFieldArray({ control, name: "invoiceGroupLinks" });

	const session = useStoreState(store => store.session);
	const { data: invoiceGroupData } = useQuery(GetInvoiceGroupTypesDocument);

	useEffect(() => {
		const types = invoiceGroupData?.invoiceGroupTypes;
		if (!types) {
			return;
		}
		types.forEach(type => {
			if (type.id == null) {
				return;
			}
			if (!fields.some(f => f.invoiceGroupTypeId === type.id)) {
				append({ invoiceGroupTypeId: type.id, linkedRelation: null });
			}
		});
	}, [ invoiceGroupData, fields, append ]);

	return (
		<React.Fragment>
			<Typography
				variant="h4"
				sx={{ fontSize: 16 }}
				color="textPrimary"
				mb={2}>
				{Language("ADDRESS")}
			</Typography>

			{hasOptionalFieldPermission(session, Fields.ADDRESS, "town") && (
				<FormGroup label={Language("CLIENT_CITY")} labelPlacement="left-center" overflow="hidden">
					<Controller
						name="address.town"
						control={control}
						render={({ field, formState }) => (
							<React.Fragment>
								<Input {...field} />
								<FormErrorMessage name={field.name} errors={formState.errors} />
							</React.Fragment>
						)} />
				</FormGroup>
			)}

			{hasOptionalFieldPermission(session, Fields.ADDRESS, "street") && (
				<FormGroup label={Language("CLIENT_STREET")} labelPlacement="left-center" overflow="hidden">
					<Controller
						name="address.street"
						control={control}
						render={({ field, formState }) => (
							<React.Fragment>
								<Input {...field} />
								<FormErrorMessage name={field.name} errors={formState.errors} />
							</React.Fragment>
						)} />
				</FormGroup>
			)}

			{hasOptionalFieldPermission(session, Fields.ADDRESS, "zipcode") && (
				<FormGroup label={Language("CLIENT_ZIPCODE")} labelPlacement="left-center" overflow="hidden">
					<Controller
						name="address.zipCode"
						control={control}
						render={({ field, formState }) => (
							<React.Fragment>
								<Input {...field} />
								<FormErrorMessage name={field.name} errors={formState.errors} />
							</React.Fragment>
						)} />
				</FormGroup>
			)}

			{hasOptionalFieldPermission(session, Fields.ADDRESS, "name1") && (
				<FormGroup label={Language("CLIENT_NAME_ONE")} labelPlacement="left-center" overflow="hidden">
					<Controller
						name="address.name1"
						control={control}
						render={({ field, formState }) => (
							<React.Fragment>
								<Input {...field} />
								<FormErrorMessage name={field.name} errors={formState.errors} />
							</React.Fragment>
						)} />
				</FormGroup>
			)}

			{hasOptionalFieldPermission(session, Fields.ADDRESS, "name2") && (
				<FormGroup label={Language("CLIENT_NAME_TWO")} labelPlacement="left-center" overflow="hidden">
					<Controller
						name="address.name2"
						control={control}
						render={({ field, formState }) => (
							<React.Fragment>
								<Input {...field} />
								<FormErrorMessage name={field.name} errors={formState.errors} />
							</React.Fragment>
						)} />
				</FormGroup>
			)}

			{hasOptionalFieldPermission(session, Fields.ADDRESS, "country") && (
				<FormGroup label={Language("CLIENT_COUNTRY")} labelPlacement="left-center" overflow="hidden">
					<Controller
						name="address.country"
						control={control}
						render={({ field }) => <CountrySelect {...field} clearable />} />
				</FormGroup>
			)}

			{hasPluginPermission(session, "invoices") && (
				<React.Fragment>
					<h4>{Language("RELATION_BANK_DETAILS")}</h4>

					<FormGroup label={Language("IVENT_ACCOUNT_HOLDER")} labelPlacement="left-center" overflow="hidden">
						<Controller
							name="accountHolder"
							control={control}
							render={({ field, formState }) => (
								<React.Fragment>
									<Input {...field} />
									<FormErrorMessage name={field.name} errors={formState.errors} />
								</React.Fragment>
							)} />
					</FormGroup>

					<FormGroup label={Language("IVENT_IBAN")} labelPlacement="left-center" overflow="hidden">
						<Controller
							name="iban"
							control={control}
							render={({ field, formState }) => (
								<React.Fragment>
									<Input {...field} />
									<FormErrorMessage name={field.name} errors={formState.errors} />
								</React.Fragment>
							)} />
					</FormGroup>

					<FormGroup label={Language("IVENT_BIC")} labelPlacement="left-center" overflow="hidden">
						<Controller
							name="bic"
							control={control}
							render={({ field, formState }) => (
								<React.Fragment>
									<Input {...field} />
									<FormErrorMessage name={field.name} errors={formState.errors} />
								</React.Fragment>
							)} />
					</FormGroup>

					<FormGroup label={Language("IVENT_VAT_REFERENCE")} labelPlacement="left-center" overflow="hidden">
						<Controller
							name="vatReference"
							control={control}
							render={({ field, formState }) => (
								<React.Fragment>
									<Input {...field} />
									<FormErrorMessage name={field.name} errors={formState.errors} />
								</React.Fragment>
							)} />
					</FormGroup>

					<FormGroup label={Language("IVENT_COC_REFERENCE")} labelPlacement="left-center" overflow="hidden">
						<Controller
							name="cocReference"
							control={control}
							render={({ field, formState }) => (
								<React.Fragment>
									<Input {...field} />
									<FormErrorMessage name={field.name} errors={formState.errors} />
								</React.Fragment>
							)} />
					</FormGroup>
				</React.Fragment>
			)}

			{(invoiceGroupData?.invoiceGroupTypes.length ?? 0) > 0 && (
				<React.Fragment>
					<h4>{Language("INVOICING")}</h4>

					{fields.map((field, index) => {
						const type = invoiceGroupData?.invoiceGroupTypes.find(t => t.id === field.invoiceGroupTypeId);
						if (!type) {
							return null;
						}
						return (
							<FormGroup key={field.id}
								label={type.name ?? ""}
								labelPlacement="left-center"
								overflow="hidden">
								<Controller
									name={`invoiceGroupLinks.${index}.linkedRelation`}
									control={control}
									render={({ field }) => (
										<RelationSelect
											{...field}
											clearable
											placeholder={name}
										/>
									)} />
							</FormGroup>
						);
					})}
				</React.Fragment>
			)}
		</React.Fragment>
	);
};

export default RelationGeneralForm;
